import React, { useState, useContext } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowDown, AlertTriangle, CheckCircle, Calculator, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { IMAGES, bgStyle } from '@/config/images';

// Plunge rate recommendations based on material and tool
const PLUNGE_DATA = {
  softwood: { factor: 0.5, name: 'Softwood', desc: 'Pine, Cedar, Spruce' },
  hardwood: { factor: 0.3, name: 'Hardwood', desc: 'Oak, Maple, Walnut' },
  plywood: { factor: 0.4, name: 'Plywood/MDF', desc: 'Sheet goods' },
  plastic: { factor: 0.4, name: 'Plastics', desc: 'Acrylic, HDPE, PVC' },
  aluminum: { factor: 0.15, name: 'Aluminum', desc: '6061, 7075' },
  brass: { factor: 0.2, name: 'Brass/Copper', desc: 'Soft metals' },
  foam: { factor: 0.8, name: 'Foam', desc: 'EPS, EVA, HDU' },
};

const PlungeRecommender = () => {
  const [material, setMaterial] = useState('softwood');
  const [toolDiameter, setToolDiameter] = useState('6.35');
  const [feedRate, setFeedRate] = useState('2000');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const feed = parseFloat(feedRate);
    const diameter = parseFloat(toolDiameter);
    const materialData = PLUNGE_DATA[material];
    
    if (!feed || !diameter || !materialData) return;

    // Base plunge rate as percentage of feed rate
    const basePlunge = feed * materialData.factor;
    
    // Adjust for tool diameter (smaller tools = slower plunge)
    const diameterFactor = Math.min(1, diameter / 6.35);
    const adjustedPlunge = basePlunge * diameterFactor;
    
    // Calculate safe and aggressive ranges
    const safePlunge = Math.round(adjustedPlunge * 0.8);
    const normalPlunge = Math.round(adjustedPlunge);
    const aggressivePlunge = Math.round(adjustedPlunge * 1.2);

    setResult({
      material: materialData,
      safe: safePlunge,
      normal: normalPlunge,
      aggressive: aggressivePlunge,
      percentOfFeed: Math.round((normalPlunge / feed) * 100),
    });
  };

  return (
    <div 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.FEEDS_SPEEDS)}
      data-testid="plunge-recommender-page"
    >
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
      
      {/* Back to Tools */}
      <Link to="/tools">
        <Button variant="ghost" className="mb-4 text-[#d6d6d6] hover:text-white hover:bg-white/10">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tools
        </Button>
      </Link>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <ArrowDown className="w-8 h-8 text-[#e67e22]" />
          <h1 className="text-3xl font-bold text-white">Plunge Rate Recommender</h1>
        </div>
        <p className="text-[#a3a3a3] max-w-2xl">
          Calculate safe plunge rates based on your material, tool size, and horizontal feed rate.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input Card */}
        <Card className="bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Calculator className="w-5 h-5 text-[#e67e22]" />
              Input Parameters
            </CardTitle>
            <CardDescription className="text-[#a3a3a3]">
              Enter your cutting parameters to get plunge rate recommendations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label className="text-[#d6d6d6]">Material Type</Label>
              <Select value={material} onValueChange={setMaterial}>
                <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                  {Object.entries(PLUNGE_DATA).map(([key, data]) => (
                    <SelectItem key={key} value={key} className="text-white">
                      {data.name} - {data.desc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label className="text-[#d6d6d6]">Tool Diameter (mm)</Label>
              <Input
                type="number"
                value={toolDiameter}
                onChange={(e) => setToolDiameter(e.target.value)}
                className="bg-black/50 border-[#3a3a3a] text-white"
                placeholder="6.35"
              />
            </div>

            <div className="grid gap-2">
              <Label className="text-[#d6d6d6]">Horizontal Feed Rate (mm/min)</Label>
              <Input
                type="number"
                value={feedRate}
                onChange={(e) => setFeedRate(e.target.value)}
                className="bg-black/50 border-[#3a3a3a] text-white"
                placeholder="2000"
              />
            </div>

            <Button 
              onClick={calculate}
              className="w-full bg-[#e67e22] hover:bg-[#d35400] text-white"
            >
              Calculate Plunge Rate
            </Button>
          </CardContent>
        </Card>

        {/* Results Card */}
        <Card className="bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <ArrowDown className="w-5 h-5 text-[#e67e22]" />
              Recommended Plunge Rates
            </CardTitle>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-4">
                <div className="p-4 bg-[#1f1f1f] border border-[#3a3a3a] rounded-sm">
                  <div className="text-sm text-[#a3a3a3] mb-1">Material</div>
                  <div className="text-lg text-white font-medium">{result.material.name}</div>
                  <div className="text-xs text-[#737373]">{result.material.desc}</div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-sm text-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mx-auto mb-2" />
                    <div className="text-xs text-green-400 mb-1">Safe</div>
                    <div className="text-2xl font-bold text-green-400">{result.safe}</div>
                    <div className="text-xs text-[#a3a3a3]">mm/min</div>
                  </div>

                  <div className="p-4 bg-[#e67e22]/10 border border-[#e67e22]/30 rounded-sm text-center">
                    <ArrowDown className="w-5 h-5 text-[#e67e22] mx-auto mb-2" />
                    <div className="text-xs text-[#e67e22] mb-1">Normal</div>
                    <div className="text-2xl font-bold text-[#e67e22]">{result.normal}</div>
                    <div className="text-xs text-[#a3a3a3]">mm/min</div>
                  </div>

                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm text-center">
                    <AlertTriangle className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                    <div className="text-xs text-amber-400 mb-1">Aggressive</div>
                    <div className="text-2xl font-bold text-amber-400">{result.aggressive}</div>
                    <div className="text-xs text-[#a3a3a3]">mm/min</div>
                  </div>
                </div>

                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-sm flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-400 mt-0.5" />
                  <div className="text-sm text-blue-200">
                    Recommended plunge is <span className="font-bold">{result.percentOfFeed}%</span> of your horizontal feed rate.
                  </div>
                </div>

                <div className="text-xs text-[#737373] space-y-1">
                  <p>• Start with the "Safe" rate for new materials or setups</p>
                  <p>• Use "Normal" for proven tool/material combinations</p>
                  <p>• "Aggressive" is for experienced users with rigid machines</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-[#a3a3a3]">
                <ArrowDown className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Enter parameters and click Calculate</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tips Section */}
      <Card className="mt-6 bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
        <CardHeader>
          <CardTitle className="text-white text-lg">Plunge Rate Tips</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-[#d6d6d6]">
            <div>
              <div className="font-medium text-[#e67e22] mb-2">When to Plunge Slower</div>
              <ul className="space-y-1 text-[#a3a3a3]">
                <li>• Smaller diameter tools (&lt;3mm)</li>
                <li>• Hard materials (metals, hardwoods)</li>
                <li>• Deep plunges (&gt;tool diameter)</li>
                <li>• Less rigid machines</li>
              </ul>
            </div>
            <div>
              <div className="font-medium text-[#e67e22] mb-2">Ramping vs Plunging</div>
              <ul className="space-y-1 text-[#a3a3a3]">
                <li>• Ramping is gentler on tools</li>
                <li>• Use helical ramp for metals</li>
                <li>• Straight plunge OK for soft materials</li>
                <li>• 2-3° ramp angle is typical</li>
              </ul>
            </div>
            <div>
              <div className="font-medium text-[#e67e22] mb-2">Tool Considerations</div>
              <ul className="space-y-1 text-[#a3a3a3]">
                <li>• Center-cutting end mills required</li>
                <li>• V-bits plunge naturally</li>
                <li>• Ball nose can plunge at full rate</li>
                <li>• Non-center-cutting must ramp</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlungeRecommender;
